import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { logError } from '@/utils/errors';
import { useTranslation } from 'react-i18next';
import authApi, { isLoginMfaRequired, type LoginCredentials, type ResetPasswordRequest } from '@/services/api/authApi';
import { useToastHelpers } from '@/components/ui/Toast';
import { resetClientAfterAuthFailure } from '@/utils/authSessionReset';
import { useCurrentUserQuery, currentUserQueryKey } from './useCurrentUserQuery';
import type { User } from '@/types/api';

// Query keys for TanStack Query
const authKeys = {
  all: ['auth'] as const,
  permissions: (roleId: string) => [...authKeys.all, 'permissions', roleId] as const,
  resetCode: (code: string) => [...authKeys.all, 'reset-code', code] as const,
};

function mapToAuthUser(user: User) {
  return {
    id: String(user.id),
    name: user.fullName,
    email: user.email,
    role: user.role?.name ?? 'user',
    companyId: String(user.companyId),
  };
}

/** Auth layer: uses shared current user query and maps to minimal auth shape */
export function useCurrentUser() {
  const result = useCurrentUserQuery();
  return {
    ...result,
    data: result.data ? mapToAuthUser(result.data) : null,
  };
}

export type LoginMutationResult =
  | {
      step: 'mfa';
      mfaToken: string;
      email: string;
      methods: Array<'email' | 'totp' | 'recovery'>;
      preferred?: 'email' | 'totp';
    }
  | { step: 'done' };

// Hook for password step of login (may return MFA challenge without issuing session)
export function useLogin() {
  const { success, error } = useToastHelpers();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials): Promise<LoginMutationResult> => {
      const response = await authApi.login(credentials);
      if (isLoginMfaRequired(response)) {
        return {
          step: 'mfa',
          mfaToken: response.mfaToken,
          email: credentials.email,
          methods: response.methods ?? ['email'],
          preferred: response.preferred,
        };
      }
      await authApi.setUserInfo(response);
      // Load the current user into the cache before the mutation finishes. A plain
      // invalidate() leaves a successful null query in "not loading" state while
      // refetch runs, so the shell keeps rendering the login screen (noticeable on
      // slow mobile connections, e.g. iOS) until the refetch eventually completes.
      await queryClient.refetchQueries({ queryKey: currentUserQueryKey });
      const currentUser = queryClient.getQueryData<User | null>(currentUserQueryKey);
      if (currentUser == null) {
        logError('Login succeeded but current user not loaded', new Error('No user in cache after login'), {
          component: 'useAuth',
          action: 'login',
        });
        throw new Error('Session could not be established');
      }
      return { step: 'done' };
    },
    onSuccess: (data) => {
      if (data.step === 'done') {
        success(t('messages.loginSuccess'));
      }
    },
    onError: (e: Error) => {
      logError('Login failed', e, { component: 'useAuth', action: 'login' });
      error(t('messages.loginFailed'));
    },
  });
}

export function useVerifyMfaLogin() {
  const { success, error } = useToastHelpers();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { mfaToken: string; code: string; method?: 'email' | 'totp' | 'recovery' }) => {
      const response = await authApi.verifyMfaLogin(payload);
      await Promise.all([authApi.setUserInfo(response), queryClient.refetchQueries({ queryKey: currentUserQueryKey })]);
      const currentUser = queryClient.getQueryData<User | null>(currentUserQueryKey);
      if (currentUser == null) {
        logError('MFA login succeeded but current user not loaded', new Error('No user in cache after MFA'), {
          component: 'useAuth',
          action: 'verifyMfa',
        });
        throw new Error('Session could not be established');
      }
      return response;
    },
    onSuccess: () => {
      success(t('messages.loginSuccess'));
    },
    onError: (e: Error) => {
      logError('MFA login failed', e, { component: 'useAuth', action: 'verifyMfa' });
      error(t('messages.mfaLoginFailed'));
    },
  });
}

export function useResendMfaCode() {
  const { success, error } = useToastHelpers();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mfaToken: string) => authApi.resendMfaCode(mfaToken),
    onSuccess: () => {
      void queryClient.invalidateQueries({ predicate: () => false });
      success(t('messages.mfaCodeResent'));
    },
    onError: (e: Error) => {
      logError('MFA resend failed', e, { component: 'useAuth', action: 'resendMfa' });
      error(t('messages.mfaResendFailed'));
    },
  });
}

// Hook for logout mutation
export function useLogout() {
  const queryClient = useQueryClient();
  const { success } = useToastHelpers();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async () => {
      try {
        // Call backend to blacklist the token
        await authApi.logout();
      } catch (error) {
        // Log the error but don't fail the logout - we still want to clear local data
        logError('Backend logout failed', error as Error, { component: 'useAuth', action: 'logout' });
      } finally {
        // Always clear local storage regardless of backend call success
        authApi.clearUserInfo();
      }
    },
    onSuccess: () => {
      resetClientAfterAuthFailure(queryClient);
      success(t('messages.logoutSuccess'));
    },
  });
}

// Hook for validating reset password code
export function useValidateResetCode(code: string | null) {
  return useQuery({
    queryKey: authKeys.resetCode(code || ''),
    queryFn: () => authApi.getResetPassword(code!),
    enabled: !!code,
    retry: false, // Don't retry failed validation
  });
}

// Hook for password reset (send email or reset with code)
export function useResetPassword() {
  const { success, error } = useToastHelpers();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: ResetPasswordRequest) => authApi.resetPassword(values),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ predicate: () => false });
      if (variables.code) {
        // Password was reset successfully
        success(t('messages.passwordResetSuccess'));
      } else {
        success(t('messages.resetEmailSent'));
      }
    },
    onError: (e: Error) => {
      logError('Password reset failed', e, { component: 'useAuth', action: 'passwordReset' });
      error(t('messages.passwordResetFailed'));
    },
  });
}

// Hook to set up authentication failure handling
export function useAuthFailureHandler() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleAuthFailure = () => {
      resetClientAfterAuthFailure(queryClient);
    };

    authApi.setAuthFailureCallback(handleAuthFailure);

    // Cleanup function
    return () => {
      authApi.setAuthFailureCallback(() => {});
    };
  }, [queryClient]);
}
